/**
 * Shared zod fragments for the product-event taxonomy (spec 68 §6.6).
 *
 * Every event schema is a plain `z.object` (non-strict): required props
 * are enforced, unknown props pass (so cross-cutting context can ride
 * along). A runtime mismatch on a required prop drops the event +
 * `log.warn('amplitude.schema_miss')` — the taxonomy never throws into a
 * request path.
 *
 * SchemaVer (§6.6.2 rule 6): every event carries a Snowplow-style
 * `MAJOR-REVISION-ADDITION`. Renames bump MAJOR; additions bump ADDITION.
 * The default is `1-0-0`.
 */
import { z } from 'zod';

export const DEFAULT_SCHEMA_VER = '1-0-0';

// ── id-shaped strings (lenient — not strict uuid, some are slugs) ─────
export const id = z.string().min(1);

// ── common enums ─────────────────────────────────────────────────────
export const roleEnum = z.enum(['owner', 'admin', 'member', 'viewer']);
export const triggerKindEnum = z.enum(['manual', 'cron', 'webhook', 'agent', 'mcp', 'slack', 'telegram', 'email']);
export const surfaceEnum = z.enum(['web', 'api', 'agent', 'mcp']);
export const scopeEnum = z.enum(['account', 'project', 'agent', 'me']);
export const openedByEnum = z.enum(['user', 'agent']);
export const flowEnum = z.enum(['oauth', 'direct']);
export const principalKindEnum = z.enum(['user', 'agent', 'team']);
export const addRemoveEnum = z.enum(['added', 'removed']);
export const toolOutcomeEnum = z.enum(['succeeded', 'failed', 'refused']);
/** Where a model-requested tool call was served (spec 106 §7.1 `tool.call.source`). */
export const toolSourceEnum = z.enum(['zero', 'skill', 'integration', 'builtin']);
export const runFailStatusEnum = z.enum(['failed', 'cancelled', 'timed_out']);

/** A microdollar amount (matches usage_event / billing units). */
export const microusd = z.number().int();
